import { apiEndpoints } from "@config/env";
import { getData } from "./global";
import type {
  PerformanceDriversTableRow,
  PointOfSail,
  PerformanceDriversEnvelopeSlice,
} from "./performanceDriversModelFit";
import { PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT } from "./performanceDriversModelFit";

export { PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT };

export interface PerformanceDriversStraightLineRow {
  twsBin: number;
  channelValues: Record<string, number | null>;
  recordCount: number | null;
  empty: boolean;
}

export function envelopeHasEmbeddedStraightLine(
  envelope: PerformanceDriversEnvelopeSlice | null | undefined
): boolean {
  if (!envelope || (envelope.schema_version ?? 0) < 6) return false;
  return (envelope.condition_cells ?? []).some(
    (cell) =>
      cell.straight_line_channels != null &&
      Object.keys(cell.straight_line_channels).length > 0 &&
      cell.straight_line_record_count != null &&
      cell.straight_line_record_count >= PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT
  );
}

export function legTypeFromPointOfSail(pointOfSail: PointOfSail): "upwind" | "downwind" | "reaching" {
  if (pointOfSail === "DOWNWIND") return "downwind";
  if (pointOfSail === "REACHING") return "reaching";
  return "upwind";
}

function parseChannelValues(raw: unknown): Record<string, number | null> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, number | null> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const num = typeof value === "number" ? value : Number(value);
    out[key] = Number.isFinite(num) ? num : null;
  }
  return out;
}

export function applyStraightLineMinRecordCount(row: PerformanceDriversStraightLineRow): PerformanceDriversStraightLineRow {
  if (
    row.empty ||
    row.recordCount == null ||
    row.recordCount < PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT
  ) {
    return { ...row, empty: true, recordCount: null, channelValues: {} };
  }
  return row;
}

export async function fetchPerformanceDriversStraightLineTable(params: {
  className: string;
  projectId: string;
  pointOfSail: PointOfSail;
  twsBins: number[];
  configurationGroup?: string;
}): Promise<PerformanceDriversStraightLineRow[]> {
  const url =
    `${apiEndpoints.app.data}/performance-drivers-straight-line` +
    `?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(params.projectId)}` +
    `&leg_type=${encodeURIComponent(legTypeFromPointOfSail(params.pointOfSail))}` +
    `&tws_bins=${encodeURIComponent(JSON.stringify(params.twsBins))}` +
    (params.configurationGroup
      ? `&config_group=${encodeURIComponent(params.configurationGroup)}`
      : "");

  const result = await getData(url);
  if (!result?.success) {
    throw new Error(result?.message ?? "Failed to load straight line data");
  }

  const payload = result.data as {
    rows?: Array<{
      tws_bin?: number;
      empty?: boolean;
      record_count?: number | null;
      channel_values?: Record<string, unknown>;
    }>;
  };

  const rawRows = Array.isArray(payload?.rows) ? payload.rows : [];
  return rawRows.map((row) => {
    const twsBin = Number(row.tws_bin);
    const recordCountRaw = row.record_count;
    const recordCount =
      recordCountRaw == null
        ? null
        : Number.isFinite(Number(recordCountRaw))
          ? Number(recordCountRaw)
          : null;
    return applyStraightLineMinRecordCount({
      twsBin,
      empty: Boolean(row.empty),
      recordCount,
      channelValues: parseChannelValues(row.channel_values),
    });
  });
}

export function mergeStraightLineIntoTableRows(
  modelRows: PerformanceDriversTableRow[],
  straightLineRows: PerformanceDriversStraightLineRow[],
  columns: string[]
): PerformanceDriversTableRow[] {
  const byTws = new Map(straightLineRows.map((row) => [row.twsBin, row]));

  return modelRows.map((modelRow) => {
    const straightLine = byTws.get(modelRow.twsBin);
    if (!straightLine) {
      return {
        ...modelRow,
        straightLineEmpty: true,
        channelValues: Object.fromEntries(columns.map((col) => [col, null])),
        sampleCount: null,
      };
    }

    const channelValues: Record<string, number | null> = {};
    for (const col of columns) {
      const value = straightLine.channelValues[col];
      channelValues[col] = value != null && Number.isFinite(value) ? value : null;
    }

    return {
      ...modelRow,
      straightLineEmpty: straightLine.empty,
      channelValues,
      sampleCount: straightLine.recordCount,
    };
  });
}
